import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './lib/queryClient';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { DesignProvider } from './contexts/DesignContext';
import { LanguageProvider } from './contexts/LanguageContext';
import LanguageSyncer from './contexts/LanguageSyncer';
import { TutorialProvider } from './contexts/TutorialContext';
import { TourProvider } from './components/tour/TourProvider';
import TourOverlay from './components/tour/TourOverlay';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ConfirmProvider } from './hooks/useConfirm';
import Layout from './components/Layout';
import './i18n';
import client from './api/client';
import './App.css';

// Core pages — direct imports for instant navigation on critical paths
import LevelUpToast from './components/leveling/LevelUpToast';
import TierUpModal from './components/leveling/TierUpModal';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CompleteSignupPage from './pages/CompleteSignupPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import WorkoutPage from './pages/WorkoutPage';
import DietPage from './pages/DietPage';
import AdaptiveMacroSettingsPage from './pages/AdaptiveMacroSettingsPage';
import ReverseDietPage from './pages/ReverseDietPage';
const TransformationPage = React.lazy(() => import('./pages/TransformationPage'));
import SocialPage from './pages/SocialPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';

// Lazy-loaded — not on the critical login→dashboard path
const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));

// Lazy-loaded pages — less frequently visited, loaded on demand
const RoutinesPage = React.lazy(() => import('./pages/RoutinesPage'));
const DietTemplatesPage = React.lazy(() => import('./pages/DietTemplatesPage'));
const SupplementTemplatesPage = React.lazy(() => import('./pages/SupplementTemplatesPage'));
const UsersPage = React.lazy(() => import('./pages/UsersPage'));
const AuditLogPage = React.lazy(() => import('./pages/AuditLogPage'));
const MyStatsPage = React.lazy(() => import('./pages/MyStatsPage'));
const InvitePage = React.lazy(() => import('./pages/InvitePage'));
const UserProfilePage = React.lazy(() => import('./pages/UserProfilePage'));
const MeasurementsPage = React.lazy(() => import('./pages/MeasurementsPage'));
const MyClientsPage = React.lazy(() => import('./pages/MyClientsPage'));
const DatabasePage = React.lazy(() => import('./pages/DatabasePage'));
const DeveloperPage = React.lazy(() => import('./pages/DeveloperPage'));
const AICoachPage = React.lazy(() => import('./pages/AICoachPage'));
const DietPlansPage = React.lazy(() => import('./pages/DietPlansPage'));
const SupplementsPage = React.lazy(() => import('./pages/SupplementsPage'));
const ExerciseLibraryPage = React.lazy(() => import('./pages/ExerciseLibraryPage'));
const CoachHubPage = React.lazy(() => import('./pages/CoachHubPage'));
const CoachTechniqueCuesPage = React.lazy(() => import('./pages/CoachTechniqueCuesPage'));
const NotificationSettingsPage = React.lazy(() => import('./pages/NotificationSettingsPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const RoutineSharePage = React.lazy(() => import('./pages/RoutineSharePage'));
const CoachProfilePage = React.lazy(() => import('./pages/CoachProfilePage'));
const PublicCoachPage = React.lazy(() => import('./pages/PublicCoachPage'));
const CoachesPage = React.lazy(() => import('./pages/CoachesPage'));
const CoachCalculatorsPage = React.lazy(() => import('./pages/CoachCalculatorsPage'));
const ClientAnalyticsPage = React.lazy(() => import('./pages/ClientAnalyticsPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const AccessibilityPage = React.lazy(() => import('./pages/AccessibilityPage'));
const AccountSettingsPage = React.lazy(() => import('./pages/AccountSettingsPage'));
const PrivacySocialPage = React.lazy(() => import('./pages/PrivacySocialPage'));
const UnitsPage = React.lazy(() => import('./pages/UnitsPage'));
const ExportImportPage = React.lazy(() => import('./pages/ExportImportPage'));
const OAuthCallbackPage = React.lazy(() => import('./pages/OAuthCallbackPage'));
const ConnectedAccountsPage = React.lazy(() => import('./pages/ConnectedAccountsPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const GettingStartedPage = React.lazy(() => import('./pages/GettingStartedPage'));
const RoutineHelpPage = React.lazy(() => import('./pages/RoutineHelpPage'));
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const CoachDowngradePage = React.lazy(() => import('./pages/CoachDowngradePage'));
const FastingPage = React.lazy(() => import('./pages/FastingPage'));
const CoachAutomationSettings = React.lazy(() => import('./pages/CoachAutomationSettings'));
const RecoveryPage = React.lazy(() => import('./pages/RecoveryPage'));
const SummaryPage = React.lazy(() => import('./pages/SummaryPage'));
const CoachQuestionnairesPage = React.lazy(() => import('./pages/CoachQuestionnairesPage'));
const QuestionnaireFillPage = React.lazy(() => import('./pages/QuestionnaireFillPage'));
const QuestionnairesPage = React.lazy(() => import('./pages/QuestionnairesPage'));
const MyCoachPage = React.lazy(() => import('./pages/MyCoachPage'));
const MyGoalsPage = React.lazy(() => import('./pages/MyGoalsPage'));
const LeaderboardPage = React.lazy(() => import('./pages/LeaderboardPage'));

function SkipOnboardingButton() {
  const [skipping, setSkipping] = React.useState(false);
  const { t } = useTranslation();
  return (
    <button disabled={skipping} onClick={async () => {
      setSkipping(true);
      try { await client.post('/onboarding/skip'); } catch {}
      window.location.href = '/dashboard';
    }}
      className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted active:bg-muted disabled:opacity-50">
      {skipping ? t('appErrors.skipping') : t('appErrors.skipForNow')}
    </button>
  );
}

function ErrorFallback({ msg, onRetry }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="text-center space-y-4 max-w-sm w-full">
        <p className="text-lg font-semibold text-foreground">{t('appErrors.somethingWentWrong')}</p>
        <p className="text-sm text-muted-foreground">{t('appErrors.tryAgainDesc')}</p>
        <div className="text-left bg-muted/40 rounded-lg px-3 py-2 text-xs text-muted-foreground font-mono break-all select-all border border-border/40">
          {msg}
        </div>
        <div className="flex gap-3 justify-center">
          <button onClick={onRetry}
            className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90">
            {t('appErrors.tryAgain')}
          </button>
          <button onClick={() => window.location.href = '/dashboard'}
            className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted active:bg-muted">
            {t('appErrors.goToDashboard')}
          </button>
        </div>
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info);
    // Auto-reload on chunk load failures (stale cache after deploy)
    if (error?.message?.includes('Loading chunk') || error?.message?.includes('ChunkLoadError')) {
      window.location.reload();
    }
  }
  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || 'Unknown error';
      return this.props.fallback || (
        <ErrorFallback msg={msg} onRetry={() => this.setState({ hasError: false, error: null })} />
      );
    }
    return this.props.children;
  }
}

const ProtectedRoute = ({ children, allowedRoles, skipOnboarding = false, noLayout = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check onboarding status for clients who haven't completed it
  if (!skipOnboarding && user.role === 'client' && !user.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return noLayout ? children : <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const LandingRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/complete-signup"
        element={<CompleteSignupPage />}
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/auth/callback/facebook"
        element={
          <PublicRoute>
            <OAuthCallbackPage provider="facebook" />
          </PublicRoute>
        }
      />
      <Route
        path="/auth/callback/instagram"
        element={
          <PublicRoute>
            <OAuthCallbackPage provider="instagram" />
          </PublicRoute>
        }
      />
      <Route
        path="/routine/:routineId"
        element={<RoutineSharePage />}
      />
      <Route
        path="/coach/:username"
        element={<PublicCoachPage />}
      />
      <Route
        path="/coaches"
        element={<CoachesPage />}
      />
      <Route
        path="/pricing"
        element={<PricingPage />}
      />
      <Route
        path="/privacy-policy"
        element={<PrivacyPolicyPage />}
      />
      <Route
        path="/privacy"
        element={<PrivacyPolicyPage />}
      />
      <Route
        path="/terms-of-service"
        element={<TermsOfServicePage />}
      />
      <Route
        path="/terms"
        element={<TermsOfServicePage />}
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute skipOnboarding={true} noLayout={true}>
            <ErrorBoundary fallback={
              <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <div className="text-center space-y-4 max-w-sm">
                  <p className="text-lg font-semibold text-foreground">Onboarding failed to load</p>
                  <p className="text-sm text-muted-foreground">You can skip this step and set up your profile later.</p>
                  <div className="flex gap-3 justify-center">
                    <button onClick={() => window.location.reload()}
                      className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90">
                      Try Again
                    </button>
                    <SkipOnboardingButton />
                  </div>
                </div>
              </div>
            }>
              <OnboardingPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - All Roles */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-stats"
        element={
          <ProtectedRoute>
            <MyStatsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/social"
        element={
          <ProtectedRoute>
            <SocialPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invite"
        element={
          <ProtectedRoute>
            <InvitePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/:userId"
        element={
          <ProtectedRoute>
            <UserProfilePage />
          </ProtectedRoute>
        }
      />
      <Route path="/clients/:id/analytics" element={<ProtectedRoute allowedRoles={['admin', 'coach']}><ClientAnalyticsPage /></ProtectedRoute>} />
      <Route path="/my-progress" element={<ProtectedRoute><ClientAnalyticsPage /></ProtectedRoute>} />
      <Route
        path="/measurements"
        element={
          <ProtectedRoute>
            <MeasurementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplements"
        element={
          <ProtectedRoute>
            <SupplementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fasting"
        element={
          <ProtectedRoute>
            <FastingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/summaries/week"
        element={
          <ProtectedRoute>
            <SummaryPage kind="week" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/summaries/month"
        element={
          <ProtectedRoute>
            <SummaryPage kind="month" />
          </ProtectedRoute>
        }
      />

      {/* Coach Profile — Coach & Admin only */}
      <Route
        path="/coach-profile"
        element={
          <ProtectedRoute allowedRoles={['admin', 'coach']}>
            <CoachProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - Coach & Admin */}
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route path="/audit-log" element={<ProtectedRoute allowedRoles={['admin']}><AuditLogPage /></ProtectedRoute>} />
      <Route
        path="/my-clients"
        element={
          <ProtectedRoute allowedRoles={['admin', 'coach']}>
            <MyClientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach-hub"
        element={
          <ProtectedRoute allowedRoles={['admin', 'coach']}>
            <CoachHubPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach/automation"
        element={
          <ProtectedRoute allowedRoles={['admin', 'coach']}>
            <CoachAutomationSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach-questionnaires"
        element={
          <ProtectedRoute allowedRoles={['admin', 'coach']}>
            <CoachQuestionnairesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/questionnaires"
        element={
          <ProtectedRoute>
            <QuestionnairesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-goals"
        element={
          <ProtectedRoute>
            <MyGoalsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            <LeaderboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-coach"
        element={
          <ProtectedRoute>
            <MyCoachPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/questionnaires/:id"
        element={
          <ProtectedRoute noLayout={true}>
            <QuestionnaireFillPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach/cues"
        element={
          <ProtectedRoute allowedRoles={['admin', 'coach']}>
            <CoachTechniqueCuesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recovery"
        element={
          <ProtectedRoute>
            <RecoveryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/database"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <DatabasePage />
          </ProtectedRoute>
        }
      />
      <Route path="/backups" element={<ProtectedRoute allowedRoles={['admin']}><Navigate to="/developer?tab=backups" replace /></ProtectedRoute>} />
      <Route path="/developer" element={<ProtectedRoute allowedRoles={['admin']}><DeveloperPage /></ProtectedRoute>} />
      <Route path="/app-summary" element={<Navigate to="/app-info" replace />} />
      <Route path="/app-info" element={<Navigate to="/developer?tab=appinfo" replace />} />
      {/* /notifications used to fall through the catch-all to /dashboard (so a
          user typing the URL directly, or any deep-link that ended up there,
          silently landed on the wrong page). Redirect to the real settings
          page so the URL is meaningful. */}
      <Route path="/notifications" element={<Navigate to="/notification-settings" replace />} />
      <Route
        path="/notification-settings"
        element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/routines"
        element={
          <ProtectedRoute allowedRoles={['admin', 'coach']}>
            <RoutinesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/diet-templates"
        element={
          <ProtectedRoute allowedRoles={['admin', 'coach']}>
            <DietTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplement-templates"
        element={
          <ProtectedRoute allowedRoles={['admin', 'coach']}>
            <SupplementTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach-calculators"
        element={
          <ProtectedRoute allowedRoles={['admin', 'coach']}>
            <CoachCalculatorsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-coach"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AICoachPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/diet-plans"
        element={
          <ProtectedRoute allowedRoles={['admin', 'coach']}>
            <DietPlansPage />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - All Roles */}
      <Route
        path="/workout"
        element={
          <ProtectedRoute>
            <WorkoutPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workout/:sessionKey"
        element={
          <ProtectedRoute>
            <WorkoutPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exercises"
        element={
          <ProtectedRoute>
            <ExerciseLibraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/diet"
        element={
          <ProtectedRoute>
            <DietPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/adaptive-macros/settings"
        element={
          <ProtectedRoute>
            <AdaptiveMacroSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reverse-diet"
        element={
          <ProtectedRoute>
            <ReverseDietPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transformation"
        element={
          <ProtectedRoute>
            <TransformationPage />
          </ProtectedRoute>
        }
      />

      {/* Subscription */}
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/coach-downgrade" element={<ProtectedRoute><CoachDowngradePage /></ProtectedRoute>} />

      {/* Settings & Preferences */}
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/accessibility" element={<ProtectedRoute><AccessibilityPage /></ProtectedRoute>} />
      <Route path="/account-settings" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
      <Route path="/settings/privacy" element={<ProtectedRoute><PrivacySocialPage /></ProtectedRoute>} />
<Route path="/settings/units" element={<ProtectedRoute><UnitsPage /></ProtectedRoute>} />
      <Route path="/settings/export" element={<ProtectedRoute><ExportImportPage /></ProtectedRoute>} />
      <Route path="/settings/connected-accounts" element={<ProtectedRoute><ConnectedAccountsPage /></ProtectedRoute>} />

      {/* Guides */}
      <Route path="/guides/getting-started" element={<ProtectedRoute><GettingStartedPage /></ProtectedRoute>} />
      <Route path="/guides/routines" element={<ProtectedRoute><RoutineHelpPage /></ProtectedRoute>} />

      {/* Help */}
      <Route path="/faq" element={<ProtectedRoute><FAQPage /></ProtectedRoute>} />
      <Route path="/contact" element={<ProtectedRoute><ContactPage /></ProtectedRoute>} />
      <Route path="/about" element={<ProtectedRoute><AboutPage /></ProtectedRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<LandingRoute />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AccessibilityProvider>
      <DesignProvider>
        <AuthProvider>
          <SubscriptionProvider>
          <LanguageProvider>
            <LanguageSyncer />
            <TooltipProvider delayDuration={200}>
            <ConfirmProvider>
            <TutorialProvider>
            <BrowserRouter>
              <TourProvider>
              <ErrorBoundary>
                <React.Suspense fallback={
                  <div className="min-h-screen flex items-center justify-center bg-background">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                  </div>
                }>
                  <AppRoutes />
                </React.Suspense>
              </ErrorBoundary>
              {/* Global leveling overlays: TierUpModal reads ?tierup= URL param and self-renders.
                  LevelUpToast needs a notifications source; Layout currently owns the notifications
                  array, so we pass an empty placeholder until a notifications context is added.
                  TODO: wire LevelUpToast to a global notifications context so it fires on level_up. */}
              <TierUpModal />
              <LevelUpToast notifications={[]} />
              <TourOverlay />
              <Toaster
                position="top-center"
                richColors
                closeButton
                duration={2500}
                toastOptions={{
                  style: {
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    color: 'hsl(var(--foreground))',
                  },
                }}
            />
              </TourProvider>
          </BrowserRouter>
            </TutorialProvider>
            </ConfirmProvider>
            </TooltipProvider>
          </LanguageProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </DesignProvider>
      </AccessibilityProvider>
    </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
